import React, { useRef, useEffect, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import { FaMapMarkerAlt, FaPlus, FaMinus, FaSearchLocation } from 'react-icons/fa';
import styled from 'styled-components';

// Set Mapbox access token directly
mapboxgl.accessToken = 'pk.eyJ1IjoidHJhdmVscG9ydHVnYWxjbHViIiwiYSI6ImNtOTZ6czVtNjAxcTMybXNpbXJnNGxwZXcifQ.lLXCMGzteF-T-x3aPGB3nw';

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  background: white;
  
  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const Sidebar = styled.div`
  width: 100%;
  background: #f8f9fa;
  overflow-y: auto;
  padding: 15px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  max-height: 300px;
  
  @media (min-width: 768px) {
    width: 300px;
    max-height: none;
    border-right: 1px solid rgba(0, 0, 0, 0.08);
    border-bottom: none;
  }
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #E31B23;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #c4121a;
  }
`;

const MapContainer = styled.div`
  flex: 1;
  position: relative;
  min-height: 300px;
  
  @media (min-width: 768px) {
    min-height: auto;
  }
`;

const CitySection = styled.div`
  margin-bottom: 20px;
  background: white;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const CityHeader = styled.div`
  font-weight: 600;
  padding: 8px 0;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  color: #2c3e50;
  font-size: 1rem;
  transition: color 0.2s ease;
  
  &:hover {
    color: #E31B23;
  }
  
  svg {
    transition: transform 0.2s ease;
  }
`;

const ActivityList = styled.div`
  margin-left: 8px;
  max-height: ${props => props.$isExpanded ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 2px solid #f0f0f0;
  padding-left: 12px;
  margin-left: 12px;
`;

const ActivityItem = styled.div`
  padding: 8px 12px;
  margin: 6px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  background: white;
  border-radius: 6px;
  transition: all 0.2s ease;
  border-left: 3px solid ${props => props.$color || '#E31B23'};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    background: #f8f9fa;
  }
  
  svg {
    margin-right: 10px;
    color: ${props => props.$color || '#E31B23'};
    font-size: 1.1em;
  }
  
  span {
    font-size: 0.9rem;
    color: #2c3e50;
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 20px;
  position: relative;
  
  input {
    width: 100%;
    padding: 10px 15px 10px 40px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 0.95rem;
    transition: all 0.3s ease;
    background: #f8f9fa;
    color: #2c3e50;
    
    &::placeholder {
      color: #95a5a6;
    }
    
    &:focus {
      outline: none;
      border-color: #E31B23;
      box-shadow: 0 0 0 3px rgba(227, 27, 35, 0.1);
      background: white;
    }
  }
  
  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #E31B23;
    font-size: 1.1rem;
  }
`;

const MapboxMap = ({ days = [], onAddActivity, onLoad, onError }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDays, setExpandedDays] = useState({});
  const [markers, setMarkers] = useState([]);
  const [geocoder, setGeocoder] = useState(null);
  const [lng] = useState(-9.1393); // Default to Lisbon
  const [lat] = useState(38.7223);
  const [zoom] = useState(12);

    // Function to get color for each day using Portuguese flag colors
  const getColorForDay = useCallback((dayIndex) => {
    const colors = [
      '#E31B23', // Portuguese Red
      '#006E33', // Portuguese Green
      '#FFD700', // Portuguese Gold
      '#5D3A9B', // Portuguese Purple
      '#E31B23', // Red (repeated for more days)
      '#006E33', // Green (repeated for more days)
      '#FFD700'  // Gold (repeated for more days)
    ];
    return colors[dayIndex % colors.length];
  }, []);

  // Group activities by day
  const activitiesByDay = useCallback(() => {
    return days.map((day, dayIndex) => {
      if (!day.slots) return { ...day, activities: [] };
      
      const activities = [];
      const dayBounds = new mapboxgl.LngLatBounds();
      
      const processActivity = (activity, slotKey, activityIndex) => {
        // Check for direct coordinates
        if (activity.lat && activity.lng) {
          const coords = [parseFloat(activity.lng), parseFloat(activity.lat)];
          dayBounds.extend(coords);
          
          activities.push({
            ...activity,
            dayIndex,
            slotKey,
            activityIndex,
            coordinates: coords,
            color: getColorForDay(dayIndex)
          });
        }
        // Check for POIs in private tours
        else if (activity.pois && Array.isArray(activity.pois)) {
          activity.pois.forEach((poi, poiIndex) => {
            if (poi.lat && poi.lng) {
              const coords = [parseFloat(poi.lng), parseFloat(poi.lat)];
              dayBounds.extend(coords);
              
              activities.push({
                ...poi,
                title: poi.name || poi.title || `POI ${poiIndex + 1}`,
                desc: poi.highlight || poi.desc || '',
                dayIndex,
                slotKey,
                activityIndex,
                poiIndex,
                coordinates: coords,
                color: getColorForDay(dayIndex),
                isPOI: true,
                parentActivity: activity.title || 'Private Tour'
              });
            }
          });
        }
      };
      
      // Process all slots in the day
      Object.entries(day.slots).forEach(([slotKey, slotActivities]) => {
        if (!Array.isArray(slotActivities)) return;
        
        slotActivities.forEach((activity, activityIndex) => {
          processActivity(activity, slotKey, activityIndex);
        });
      });
      
      return {
        ...day,
        activities,
        bounds: activities.length > 0 ? dayBounds : null
      };
    });
  }, [days, getColorForDay]);

  // Add markers to map when activities change
  useEffect(() => {
    if (!map.current) return;

    // Clear existing markers
    markers.forEach(marker => marker.remove());
    const newMarkers = [];

    // Add markers for all activities
    activitiesByDay().forEach(day => {
      day.activities.forEach(activity => {
        // Create popup content
        const popupContent = document.createElement('div');
        popupContent.className = 'map-popup';
        
        if (activity.isPOI) {
          popupContent.innerHTML = `
            <div style="padding: 8px; min-width: 200px;">
              <div style="font-weight: 600; color: #E31B23; margin-bottom: 4px;">
                ${activity.parentActivity}
              </div>
              <div style="font-weight: 500; margin-bottom: 6px;">
                ${activity.title}
              </div>
              ${activity.desc ? `<div style="font-size: 0.9em; color: #555; margin-bottom: 6px;">${activity.desc}</div>` : ''}
              ${activity.duration ? `<div style="font-size: 0.85em; color: #777; display: flex; align-items: center; gap: 4px;">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                ${activity.duration}
              </div>` : ''}
            </div>
          `;
        } else {
          popupContent.innerHTML = `
            <div style="padding: 8px; min-width: 200px;">
              <div style="font-weight: 600; margin-bottom: 4px;">
                ${activity.title}
              </div>
              ${activity.desc ? `<div style="font-size: 0.9em; color: #555; margin-bottom: 6px;">${activity.desc}</div>` : ''}
              ${activity.duration ? `<div style="font-size: 0.85em; color: #777; display: flex; align-items: center; gap: 4px;">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                ${activity.duration}
              </div>` : ''}
            </div>
          `;
        }
        
        const popup = new mapboxgl.Popup({ offset: 25 })
          .setDOMContent(popupContent);
        
        const marker = new mapboxgl.Marker({
          color: activity.color || getColorForDay(activity.dayIndex)
        })
          .setLngLat(activity.coordinates)
          .setPopup(popup)
          .addTo(map.current);

        marker.getElement().addEventListener('click', () => {
          setSelectedMarker(activity);
        });

        newMarkers.push(marker);
      });
    });

    setMarkers(newMarkers);

    // Fit map to show all markers if there are any
    if (newMarkers.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      activitiesByDay().forEach(day => {
        if (day.bounds) {
          bounds.extend(day.bounds.getNorthEast());
          bounds.extend(day.bounds.getSouthWest());
        }
      });
      
      map.current.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 350, right: 50 },
        duration: 1000
      });
    }

    return () => {
      newMarkers.forEach(marker => marker.remove());
    };
  }, [days]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current) return;

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [lng, lat],
        zoom: zoom,
        minZoom: 6,
        maxZoom: 16,
        maxBounds: [
          [-31.3, 36.9], // Southwest coordinates of Portugal
          [-6.2, 42.2]   // Northeast coordinates of Portugal
        ]
      });
      
      // Add navigation control
      map.current.addControl(new mapboxgl.NavigationControl({
        showCompass: true,
        showZoom: true,
        visualizePitch: true
      }), 'top-right');
      
      // Add fullscreen control
      map.current.addControl(new mapboxgl.FullscreenControl(), 'top-right');
      
      // Add scale control
      map.current.addControl(new mapboxgl.ScaleControl({
        maxWidth: 100,
        unit: 'metric'
      }), 'bottom-left');
      
      console.log('Map instance created:', map.current ? 'Success' : 'Failed');
      
      // Add error event listener
      map.current.on('error', (e) => {
        console.error('Map error:', e.error);
      });
      
      // Add load event
      map.current.on('load', () => {
        console.log('Map loaded successfully');
        if (onLoad) onLoad();
      });
      
    } catch (error) {
      console.error('Error initializing map:', error);
      if (onError) onError(error);
      return;
    }

    // Initialize geocoder
    const geocoderInstance = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl: mapboxgl,
      placeholder: 'Search for a place in Portugal',
      countries: 'pt',
      bbox: [-9.5, 36.8, -6.2, 42.2], // Rough bounding box for Portugal
      proximity: {
        longitude: -9.1393,
        latitude: 38.7223
      }
    });
    
    map.current.addControl(geocoderInstance, 'top-left');
    setGeocoder(geocoderInstance);
    
    // Store the geocoder instance in a ref to avoid dependency issues
    const currentGeocoder = geocoderInstance;
    
    // Clean up on unmount
    return () => {
      try {
        // First remove the geocoder if it exists
        if (currentGeocoder && map.current) {
          map.current.removeControl(currentGeocoder);
        }
        
        // Then remove the map
        if (map.current) {
          map.current.remove();
          map.current = null;
        }
        
        // Clear the geocoder state
        setGeocoder(null);
      } catch (error) {
        console.error('Error during cleanup:', error);
      }
    };
  }, [lng, lat, zoom]);
  
  // Add markers to map when days change
  useEffect(() => {
    if (!map.current) return;
    
    // Remove existing markers
    markers.forEach(marker => marker.remove());
    
    const newMarkers = [];
    const daysData = activitiesByDay();
    
    // Add markers for each activity in each day
    daysData.forEach(day => {
      day.activities.forEach((activity) => {
        if (!activity.coordinates) return;
        
        const color = getColorForDay(activity.dayIndex);
        
        // Create a DOM element for the marker
        const el = document.createElement('div');
        el.className = 'marker';
        el.style.backgroundColor = color;
        el.style.width = '16px';
        el.style.height = '16px';
        el.style.borderRadius = '50%';
        el.style.border = '2px solid white';
        el.style.cursor = 'pointer';
        
        // Create the marker
        const marker = new mapboxgl.Marker({
          element: el,
          anchor: 'center'
        })
          .setLngLat(activity.coordinates)
          // Create popup with Portuguese theme
          .setPopup(
            new mapboxgl.Popup({ 
              offset: 25,
              className: 'mapbox-popup',
              closeButton: true,
              closeOnClick: false,
              maxWidth: '300px'
            }).setHTML(`
              <div class="popup-content">
                <h3 style="color: #E31B23; margin: 0 0 8px 0; font-size: 1.1rem;">
                  ${activity.name}
                </h3>
                <p style="margin: 0 0 12px 0; color: #2c3e50; font-size: 0.9rem;">
                  ${activity.address || 'No address available'}
                </p>
                <button 
                  class="add-to-itinerary" 
                  data-activity='${JSON.stringify(activity)}'
                  style="
                    background: linear-gradient(135deg, #E31B23, #FF6B6B);
                    color: white;
                    border: none;
                    padding: 8px 16px;
                    border-radius: 20px;
                    font-size: 0.9rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    width: 100%;
                    font-weight: 500;
                  "
                  onmouseover="this.style.background='linear-gradient(135deg, #c4121a, #e63946)'"
                  onmouseout="this.style.background='linear-gradient(135deg, #E31B23, #FF6B6B)'"
                >
                  Add to Itinerary
                </button>
              </div>
            `)
          )
          .addTo(map.current);
        
        // Add click handler
        el.addEventListener('click', () => {
          setSelectedMarker(activity);
          map.current.flyTo({
            center: activity.coordinates,
            zoom: 14,
            essential: true
          });
        });
        
        newMarkers.push(marker);
      });
    });
    
    setMarkers(newMarkers);
    
    // Add event listener for add to itinerary button in popup
    const handleAddToItinerary = (e) => {
      if (onAddActivity) {
        onAddActivity(e.detail);
      }
    };
    
    document.addEventListener('addToItinerary', handleAddToItinerary);
    
    // Clean up
    return () => {
      document.removeEventListener('addToItinerary', handleAddToItinerary);
    };
  }, [days, activitiesByDay, onAddActivity]);
  
  // Handle search
  const handleSearch = useCallback((e) => {
    setSearchQuery(e.target.value.toLowerCase());
  }, []);
  
  // Filter activities by search query
  const filteredCities = useCallback(() => {
    const days = activitiesByDay();
    const query = searchQuery.toLowerCase().trim();
    
    if (!query) return days;
    
    return days.map(day => {
      const filteredActivities = day.activities.filter(activity => 
        (activity.title && activity.title.toLowerCase().includes(query)) ||
        (activity.desc && activity.desc.toLowerCase().includes(query)) ||
        (activity.location && activity.location.toLowerCase().includes(query))
      );
      
      return {
        ...day,
        activities: filteredActivities
      };
    }).filter(day => day.activities.length > 0);
  }, [searchQuery, activitiesByDay]);
  
  // Handle adding an activity to the itinerary
  const handleAddActivityClick = (activity) => {
    if (onAddActivity) {
      onAddActivity(activity);
    }
  };
  
  // Handle clicking on a city to zoom to its bounds
  const zoomToCity = useCallback((cityData) => {
    if (!map.current || !cityData.bounds || cityData.coordinates.length === 0) return;
    
    if (cityData.coordinates.length === 1) {
      // If only one point, zoom to that point
      map.current.flyTo({
        center: cityData.coordinates[0],
        zoom: 14,
        essential: true
      });
    } else {
      // If multiple points, fit bounds
      map.current.fitBounds(cityData.bounds, {
        padding: { top: 50, bottom: 50, left: 350, right: 50 }
      });
    }
  }, []);

  // Toggle day expansion
  const toggleDay = useCallback((dayIndex) => {
    setExpandedDays(prev => ({
      ...prev,
      [dayIndex]: !prev[dayIndex]
    }));
  }, []);

  const cities = filteredCities();

  return (
    <Container>
      <Sidebar>
        <h3 style={{ margin: '0 0 15px 0', paddingBottom: '10px', borderBottom: '1px solid #eee' }}>
          Trip Itinerary
        </h3>
        
        <SearchContainer>
          <FaSearchLocation />
          <input 
            type="text" 
            placeholder="Search activities..." 
            value={searchQuery}
            onChange={handleSearch}
          />
        </SearchContainer>
        
      <div>
        {cities.map((day, dayIndex) => (
          <CitySection key={`day-${dayIndex}`}>
            <CityHeader 
              onClick={() => {
                if (day.bounds) {
                  map.current.fitBounds(day.bounds, {
                    padding: 50,
                    duration: 1000
                  });
                }
                toggleDay(dayIndex);
              }}
            >
              <span>Day {dayIndex + 1}</span>
              {expandedDays[dayIndex] ? <FaMinus size={12} /> : <FaPlus size={12} />}
            </CityHeader>
            <ActivityList $isExpanded={expandedDays[dayIndex]}>
              {day.activities.map((activity, activityIndex) => (
                <ActivityItem 
                  key={`activity-${activityIndex}`}
                  onClick={() => {
                    setSelectedMarker(activity);
                    map.current.flyTo({
                      center: activity.coordinates,
                      zoom: 14,
                      essential: true
                    });
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FaMapMarkerAlt color={getColorForDay(dayIndex)} style={{ marginRight: '8px' }} />
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{activity.title || 'Untitled Activity'}</div>
                      {activity.time && (
                        <div style={{ fontSize: '0.8em', color: '#666' }}>
                          {activity.time}
                        </div>
                      )}
                    </div>
                  </div>
                </ActivityItem>
              ))}
            </ActivityList>
          </CitySection>
        ))}
          
        {cities.length === 0 && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            No activities found. Try a different search term or add some activities.
          </div>
        )}
      </div>
    </Sidebar>
      
    <MapContainer ref={mapContainer}>
      {selectedMarker && (
        <div
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            maxWidth: '300px',
            zIndex: 1000,
          }}
        >
          <h3 style={{ margin: '0 0 10px 0', color: getColorForDay(selectedMarker.dayIndex) }}>
            {selectedMarker.title}
          </h3>
          {selectedMarker.desc && (
            <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#666' }}>
              {selectedMarker.desc}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <FaMapMarkerAlt style={{ color: '#666', marginRight: '5px' }} />
            <span style={{ fontSize: '12px', color: '#666' }}>
              {selectedMarker.coordinates[1].toFixed(4)}, {selectedMarker.coordinates[0].toFixed(4)}
            </span>
          </div>
          <button
            onClick={() => {
              if (onAddActivity) {
                onAddActivity(selectedMarker);
              }
              setSelectedMarker(null);
            }}
            style={{
              backgroundColor: getColorForDay(selectedMarker.dayIndex),
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              width: '100%',
            }}
          >
            Add to Itinerary
          </button>
        </div>
      )}
    </MapContainer>
  </Container>
);

};

export default MapboxMap;
